import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import authService from '../services/AuthService';
import { useAuthBranding } from '../hooks/useAuthBranding';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Hover/focus states
  const [emailFocused, setEmailFocused] = useState(false);
  const [submitHovered, setSubmitHovered] = useState(false);
  const [backHovered, setBackHovered] = useState(false);

const { branding, brandingLoaded } = useAuthBranding('Forgot Password');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await authService.requestPasswordReset(email);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/auth');
  };

  // Styles
  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--v2-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'var(--font-sans)',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '40px',
    width: '100%',
    maxWidth: '440px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 8px 0',
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--v2-text-tertiary)',
    margin: '0 0 24px 0',
    textAlign: 'center',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  const getInputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    border: focused ? '1px solid var(--v2-primary)' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const getButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '12px 20px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  const getSecondaryButtonStyle = (hovered: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  const linkStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: backHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: backHovered ? 'underline' : 'none',
    padding: 0,
  };

  const alertStyle = (type: 'error' | 'success'): React.CSSProperties => ({
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: `4px solid ${type === 'error' ? 'var(--v2-error)' : 'var(--v2-success)'}`,
    backgroundColor: type === 'error' ? 'var(--v2-error-bg)' : 'var(--v2-success-bg)',
    color: type === 'error' ? 'var(--v2-error)' : 'var(--v2-success-dark)',
    fontSize: '0.875rem',
    marginBottom: '20px',
  });

  // Success state
  if (success) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h1 style={titleStyle}>Check Your Email</h1>
            
            <div style={{ ...alertStyle('success'), width: '100%', boxSizing: 'border-box', marginTop: '8px' }}>
              Password reset instructions have been sent to <strong>{email}</strong>. 
              Check your email and follow the link to reset your password.
            </div>

            <button
              onClick={handleBackToLogin}
              onMouseEnter={() => setBackHovered(true)}
              onMouseLeave={() => setBackHovered(false)}
              style={getSecondaryButtonStyle(backHovered)}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  const isSubmitDisabled = loading || !email;

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Forgot Password</h1>
          <p style={subtitleStyle}>
            Enter your email address and we'll send you instructions to reset your password.
          </p>

          {error && (
            <div style={alertStyle('error')}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                disabled={loading}
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                style={getInputStyle(emailFocused)}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitDisabled}
              onMouseEnter={() => setSubmitHovered(true)}
              onMouseLeave={() => setSubmitHovered(false)}
              style={getButtonStyle(submitHovered, isSubmitDisabled)}
            >
              {loading ? (
                <>
                  <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>Sending...</span>
                </>
              ) : (
                'Send Reset Instructions'
              )}
            </button>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button
                type="button"
                onClick={handleBackToLogin}
                onMouseEnter={() => setBackHovered(true)}
                onMouseLeave={() => setBackHovered(false)}
                style={linkStyle}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
